// src/components/CoverStylePicker.js
import React, { useMemo } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WesternCornerDecor from "./WesternCornerDecor";

export const DEFAULT_COVER_URL =
  'https://firebasestorage.googleapis.com/v0/b/scan-cap.appspot.com/o/STYLE%20PAGE%2Felevate_couple_900kb.jpg?alt=media&token=1a79e16b-789d-444e-a5f5-20416b2360e9';

export const STYLE_PRESETS = [
  {
    id: "frozen",
    name: "Frozen",
    bg: "radial-gradient(circle at top, #38bdf8 0%, #0f172a 60%, #020617 100%)",
    titleFont: '"Gill Sans","SF Pro Text",system-ui,sans-serif',
    dateFont: '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 24,
    dateFontSize: 12,
    titleColor: "#e0f2fe",
    dateColor: "#bae6fd",
    welcomeColor: "#e0f2fe",
    buttonColor: "#e0f2fe",
    buttonFontColor: "#0f172a",
    secondaryButtonBgColor: "transparent",
    secondaryButtonFontColor: "#e0f2fe",
    layout: "frozen",
  },
  {
    id: "sage",
    name: "Sage",
    bg: "radial-gradient(circle at top, #789785 0%, #385443 48%, #08100b 100%)",
    titleFont:
      '"Great Vibes","SF Pro Display",-apple-system,system-ui,sans-serif',
    dateFont: '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 24,
    dateFontSize: 12,
    titleColor: "#f1faf5",
    dateColor: "rgba(241,250,245,0.82)",
    welcomeColor: "#f1faf5",
    buttonColor: "#f1faf5",
    buttonFontColor: "#0b1510",
    secondaryButtonBgColor: "transparent",
    secondaryButtonFontColor: "#f1faf5",
    layout: "color",
  },
  {
    id: "blush",
    name: "Blush",
    bg: "radial-gradient(circle at top, #d95c95 0%, #8f2456 48%, #160610 100%)",
    titleFont:
      '"Great Vibes","SF Pro Display",-apple-system,system-ui,sans-serif',
    dateFont: '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 24,
    dateFontSize: 12,
    titleColor: "#fff7fb",
    dateColor: "rgba(255,247,251,0.84)",
    welcomeColor: "#fff7fb",
    buttonColor: "#fff7fb",
    buttonFontColor: "#160610",
    secondaryButtonBgColor: "transparent",
    secondaryButtonFontColor: "#fff7fb",
    layout: "color",
  },
  {
    id: "classic",
    name: "Classic",
    bg: "radial-gradient(circle at top, #0f172a 0%, #020617 70%, #000000 100%)",
    titleFont:
      '"SF Pro Display","SF Pro Text",-apple-system,system-ui,sans-serif',
    dateFont: '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 24,
    dateFontSize: 12,
    titleColor: "#f9fafb",
    dateColor: "#9ca3af",
    welcomeColor: "#f9fafb",
    buttonColor: "#111827",
    buttonFontColor: "#f9fafb",
    secondaryButtonBgColor: "#020617",
    secondaryButtonFontColor: "#e5e7eb",
    layout: "classic",
  },
  {
    id: "western",
    name: "Western",
    bg: "linear-gradient(180deg, #4e2f16 0%, #2a1b10 100%)",
    titleFont:
      '"Cinzel Decorative","Cinzel","Trajan Pro","Playfair Display","Times New Roman",serif',
    dateFont:
      '"Cinzel","Roboto Slab","Times New Roman",serif',
    titleFontSize: 24,
    dateFontSize: 13,
    titleColor: "#fef3c7",
    dateColor: "#fed7aa",
    welcomeColor: "#fef3c7",
    buttonColor: "#ffffff",
    buttonFontColor: "#111827",
    secondaryButtonBgColor: "#ffffff",
    secondaryButtonFontColor: "#111827",
    layout: "western",
  },
  {
    id: "champagne",
    name: "Champagne",
    bg: "radial-gradient(circle at top, #f9e7dd 0%, #fdf2f8 35%, #111827 100%)",
    titleFont:
      '"Playfair Display","Cormorant Garamond","Times New Roman",serif',
    dateFont: '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 24,
    dateFontSize: 12,
    titleColor: "#fdf2f8",
    dateColor: "#fee2e2",
    welcomeColor: "#fdf2f8",
    buttonColor: "#fef3c7",
    buttonFontColor: "#111827",
    secondaryButtonBgColor: "transparent",
    secondaryButtonFontColor: "#fef2f2",
    layout: "color",
  },
  {
    id: "polaroid",
    name: "Polaroid",
    bg: "linear-gradient(180deg, #0f172a 0%, #1e3a8a 100%)",
    titleFont:
      '"SF Pro Text",-apple-system,system-ui,sans-serif',
    dateFont:
      '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 16,
    dateFontSize: 11,
    titleColor: "#111827",
    dateColor: "#6b7280",
    welcomeColor: "#111827",
    buttonColor: "#ffffff",
    buttonFontColor: "#111827",
    secondaryButtonBgColor: "#ffffff",
    secondaryButtonFontColor: "#111827",
    layout: "polaroid",
  },
  {
    id: "blacktie",
    name: "Elegant",
    bg: "radial-gradient(circle at top, #294a82 0%, #13274d 48%, #030712 100%)",
    titleFont:
      '"Great Vibes","SF Pro Display",-apple-system,system-ui,sans-serif',
    dateFont: '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 24,
    dateFontSize: 12,
    titleColor: "#f9fafb",
    dateColor: "#e5e7eb",
    welcomeColor: "#f9fafb",
    buttonColor: "#f9fafb",
    buttonFontColor: "#020617",
    secondaryButtonBgColor: "transparent",
    secondaryButtonFontColor: "#e5e7eb",
    layout: "color",
  },
  {
    id: "scarlet",
    name: "Scarlet",
    bg: "radial-gradient(circle at top, #b31225 0%, #650811 48%, #120204 100%)",
    titleFont:
      '"Great Vibes","SF Pro Display",-apple-system,system-ui,sans-serif',
    dateFont: '"SF Pro Text",-apple-system,system-ui,sans-serif',
    titleFontSize: 24,
    dateFontSize: 12,
    titleColor: "#fff1f2",
    dateColor: "rgba(255,241,242,0.82)",
    welcomeColor: "#fff1f2",
    buttonColor: "#fff1f2",
    buttonFontColor: "#09070a",
    secondaryButtonBgColor: "transparent",
    secondaryButtonFontColor: "#fff1f2",
    layout: "color",
  },
];

function buildCustomizationFromPreset(preset, prev = {}) {
  return {
    ...prev,
    coverStyleId: preset.id,
    backgroundColor: preset.bg,
    titleFont: preset.titleFont,
    dateFont: preset.dateFont,
    welcomeFont: preset.titleFont,
    titleFontSize: `${preset.titleFontSize || 18}px`,
    dateFontSize: `${preset.dateFontSize || 12}px`,
    welcomeFontSize: `${(preset.titleFontSize || 18) - 2}px`,
    titleFontColor: preset.titleColor,
    dateFontColor: preset.dateColor,
    welcomeFontColor: preset.welcomeColor,
    buttonColor: preset.buttonColor,
    buttonFontColor: preset.buttonFontColor || "#ffffff",
    buttonGlowColor: preset.buttonColor,
    secondaryButtonBgColor:
      preset.secondaryButtonBgColor,
    secondaryButtonFontColor:
      preset.secondaryButtonFontColor,
  };
}

function getPhotoOpacity(isPlaceholder) {
  return isPlaceholder ? 0.98 : 1;
}

function getPhotoBlur(isPlaceholder, blurPx = 8) {
  return isPlaceholder ? `blur(${blurPx}px)` : "none";
}

function PhoneShell({ children }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: 220,
        height: 440,
        borderRadius: 8,
        mx: "auto",
        boxShadow: "0 18px 60px rgba(0,0,0,0.65)",
        backgroundColor: "#020617",
        border: "1px solid rgba(148,163,184,0.45)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 10,
          left: "50%",
          transform: "translateX(-50%)",
          width: 88,
          height: 18,
          borderRadius: 999,
          backgroundColor: "#020617",
          zIndex: 20,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function PreviewButtons({
  preset,
  bottom = 24,
  primaryBackground,
  primaryColor,
  secondaryBackground,
  secondaryColor,
  secondaryBorder,
}) {
  const resolvedPrimaryBackground =
    primaryBackground || preset.buttonColor;

  const resolvedPrimaryColor =
    primaryColor || preset.buttonFontColor || "#111827";

  const resolvedSecondaryBackground =
    secondaryBackground !== undefined
      ? secondaryBackground
      : preset.secondaryButtonBgColor !== undefined
        ? preset.secondaryButtonBgColor
        : "transparent";

  const resolvedSecondaryColor =
    secondaryColor ||
    preset.secondaryButtonFontColor ||
    "#ffffff";

  const resolvedSecondaryBorder =
    secondaryBorder ||
    `1.5px solid ${resolvedSecondaryColor}`;

  return (
    <Box
      sx={{
        position: "absolute",
        left: 16,
        right: 16,
        bottom,
        display: "flex",
        flexDirection: "column",
        gap: 0.6,
        zIndex: 10,
      }}
    >
      <Button
        fullWidth
        sx={{
          minHeight: 29,
          textTransform: "none",
          fontWeight: 700,
          fontSize: 12,
          lineHeight: 1.1,
          borderRadius: 999,
          py: 0.55,
          background: resolvedPrimaryBackground,
          color: resolvedPrimaryColor,
          boxShadow: "0 10px 30px rgba(0,0,0,0.46)",
          "&:hover": {
            background: resolvedPrimaryBackground,
            opacity: 0.98,
          },
        }}
      >
        Upload photos
      </Button>

      <Button
        fullWidth
        sx={{
          minHeight: 29,
          textTransform: "none",
          fontWeight: 600,
          fontSize: 12,
          lineHeight: 1.1,
          borderRadius: 999,
          py: 0.55,
          background: resolvedSecondaryBackground,
          color: resolvedSecondaryColor,
          border: resolvedSecondaryBorder,
          "&:hover": {
            background: resolvedSecondaryBackground,
            opacity: 0.98,
          },
        }}
      >
        Open camera
      </Button>
    </Box>
  );
}

function FullBleedLayout({
  preset,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const photoBg = coverPhotoUrl || DEFAULT_COVER_URL;

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${photoBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: isPlaceholder
            ? `brightness(0.7) ${getPhotoBlur(
                isPlaceholder,
                6.8
              )}`
            : "brightness(0.8)",
          opacity: getPhotoOpacity(isPlaceholder),
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.88) 100%)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          left: 16,
          right: 16,
          bottom: 92,
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            color: preset.titleColor,
            fontFamily: preset.titleFont,
            fontWeight: 700,
            fontSize: preset.titleFontSize,
            letterSpacing: 0.2,
            textShadow: "0 2px 10px rgba(0,0,0,0.9)",
          }}
        >
          {title}
        </Typography>

        {!!date && (
          <Typography
            sx={{
              mt: 0.3,
              color: preset.dateColor,
              fontFamily: preset.dateFont,
              fontWeight: 500,
              fontSize: preset.dateFontSize,
              textShadow: "0 1px 6px rgba(0,0,0,0.9)",
            }}
          >
            {date}
          </Typography>
        )}
      </Box>

      <PreviewButtons preset={preset} />
    </>
  );
}

function PolaroidLayout({
  preset,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const photoBg = coverPhotoUrl || DEFAULT_COVER_URL;

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: preset.bg,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 78,
          left: "50%",
          transform: "translateX(-50%) rotate(-3deg)",
          width: "78%",
          aspectRatio: "3 / 4",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          p: 1.1,
          background:
            "linear-gradient(180deg, #ffffff 0%, #f4f3ef 100%)",
          border: "1px solid rgba(255,255,255,0.8)",
          boxShadow:
            "0 18px 42px rgba(0,0,0,0.48), 0 2px 8px rgba(0,0,0,0.22)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "0 0 72%",
            overflow: "hidden",
            mb: 0.8,
            backgroundColor: "#e5e7eb",
          }}
        >
          <Box
            component="img"
            src={photoBg}
            alt="Cover"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              filter: isPlaceholder
                ? `brightness(0.7) ${getPhotoBlur(
                    isPlaceholder,
                    6.8
                  )}`
                : "none",
              opacity: getPhotoOpacity(isPlaceholder),
            }}
          />
        </Box>

        <Typography
          sx={{
            maxWidth: "100%",
            color: preset.titleColor,
            fontFamily: preset.titleFont,
            fontSize: preset.titleFontSize,
            fontWeight: 600,
            lineHeight: 1.15,
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>

        {!!date && (
          <Typography
            sx={{
              mt: 0.25,
              color: preset.dateColor,
              fontFamily: preset.dateFont,
              fontSize: preset.dateFontSize,
              fontWeight: 500,
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            {date}
          </Typography>
        )}
      </Box>

      <PreviewButtons
        preset={preset}
        primaryBackground="#ffffff"
        primaryColor="#111827"
        secondaryBackground="#ffffff"
        secondaryColor="#111827"
        secondaryBorder="1.5px solid rgba(15,23,42,0.8)"
      />
    </>
  );
}

function ColorLayout({
  preset,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const photoBg = coverPhotoUrl || DEFAULT_COVER_URL;

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: preset.bg,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
     background:
  preset.id === "champagne"
    ? "linear-gradient(180deg, rgba(15,23,42,0.05) 0%, rgba(15,23,42,0.65) 70%, rgba(0,0,0,0.9) 100%)"
    : "linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.22) 62%, rgba(0,0,0,0.72) 100%)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 48,
          left: "50%",
          transform: "translateX(-50%)",
          width: "89%",
          aspectRatio: "5 / 6",
          overflow: "hidden",
          borderRadius: 2,
          backgroundColor: "#020617",
          border: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "0 14px 34px rgba(0,0,0,0.55)",
        }}
      >
        <Box
          component="img"
          src={photoBg}
          alt="Cover"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition:
              preset.id === "blush"
                ? "50% 18%"
                : "50% 20%",
            display: "block",
            filter: isPlaceholder
              ? `brightness(0.7) ${getPhotoBlur(
                  isPlaceholder,
                  6.8
                )}`
              : "none",
            opacity: getPhotoOpacity(isPlaceholder),
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 42%, rgba(0,0,0,0.16) 62%, rgba(0,0,0,0.88) 100%)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: 12,
            right: 12,
            bottom: 12,
            textAlign: "left",
          }}
        >
          <Typography
            sx={{
              fontFamily: preset.titleFont,
              fontSize: 24,
              fontWeight: 500,
              lineHeight: 1.02,
              color: "#ffffff",
              textShadow: "0 2px 10px rgba(0,0,0,0.85)",
            }}
          >
            {title}
          </Typography>

          {!!date && (
            <Typography
              sx={{
               mt: 0.6,
                fontFamily: preset.dateFont,
                fontSize: 9,
                fontWeight: 500,
                lineHeight: 1.2,
                letterSpacing: 1.35,
                color: "rgba(255,255,255,0.9)",
                textShadow: "0 1px 8px rgba(0,0,0,0.8)",
              }}
            >
              — {date} —
            </Typography>
          )}
        </Box>
      </Box>

      <PreviewButtons
  preset={preset}
  bottom={73}
  {...(preset.id === "champagne"
    ? {
        primaryBackground:
          "linear-gradient(90deg, #fef3c7, #fed7e2)",
        primaryColor: "#111827",
        secondaryBackground: "transparent",
        secondaryColor: "#fef2f2",
        secondaryBorder: "1.5px solid rgba(254,202,202,0.9)",
      }
    : {})}
/>

      <Box
        sx={{
          position: "absolute",
          left: 16,
          right: 16,
          bottom: 16,
          height: 42,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 0.25,
          borderTop: "1px solid rgba(255,255,255,0.16)",
          borderBottom:
            "1px solid rgba(255,255,255,0.16)",
          color: "#ffffff",
        }}
      >
        <Box
          sx={{
            width: 29,
            height: 29,
            flexShrink: 0,
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.11)",
          }}
        >
          <ImageOutlinedIcon sx={{ fontSize: 17 }} />
        </Box>

        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          View gallery
        </Typography>

        <ChevronRightIcon
          sx={{
            ml: "auto",
            fontSize: 19,
          }}
        />
      </Box>
    </>
  );
}

function WesternLayout({
  preset,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const photoBg = coverPhotoUrl || DEFAULT_COVER_URL;

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "#c48a5a",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 150,
       background:
  "linear-gradient(180deg, #2a1b10 0%, #4e2f16 70%, rgba(78,47,22,0) 100%)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 120,
          bottom: 110,
          left: 0,
          right: 0,
          background:
            "linear-gradient(180deg, #d9b38c 0%, #c48a5a 100%)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 140,
          background:
  "linear-gradient(0deg, #3b2514 0%, #4e2f16 72%, rgba(78,47,22,0) 100%)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 70,
          left: "50%",
          transform: "translateX(-50%)",
          width: "84%",
          textAlign: "center",
          zIndex: 10,
        }}
      >
        <Typography
          sx={{
            fontFamily: preset.titleFont,
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 2,
            lineHeight: 1.15,
            color: "#ffffff",
            textShadow: "0 2px 10px rgba(0,0,0,0.65)",
          }}
        >
          {title}
        </Typography>

        {!!date && (
          <Typography
            sx={{
              mt: 0.25,
              fontFamily: preset.dateFont,
              fontSize: 12,
              fontWeight: 600,
              color: "#ffffff",
              opacity: 0.95,
              textShadow:
                "0 2px 10px rgba(0,0,0,0.65)",
            }}
          >
            {date}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 148,
          left: 24,
          right: 24,
          bottom: 124,
          overflow: "visible",
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 30,
          }}
        >
          <WesternCornerDecor
            color="#5a3418"
            opacity={0.9}
            bleed={18}
            scale={1.1}
            zIndex={30}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 10,
            overflow: "hidden",
            backgroundColor: "#f5e6d3",
            border: "1px solid #5a3418",
            borderRadius: 1,
            boxShadow: "0 8px 18px rgba(0,0,0,0.3)",
            zIndex: 10,
          }}
        >
          <Box
            component="img"
            src={photoBg}
            alt="Cover"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "50% 30%",
              display: "block",
              filter: isPlaceholder
                ? `brightness(0.7) ${getPhotoBlur(
                    isPlaceholder,
                    6.8
                  )}`
                : "none",
              opacity: getPhotoOpacity(isPlaceholder),
            }}
          />
        </Box>
      </Box>

      <PreviewButtons
        preset={preset}
        bottom={8}
        primaryBackground="#ffffff"
        primaryColor="#111827"
        secondaryBackground="#ffffff"
        secondaryColor="#111827"
        secondaryBorder="1.5px solid #5a3418"
      />
    </>
  );
}

function FrozenLayout({
  preset,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const photoBg = coverPhotoUrl || DEFAULT_COVER_URL;

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: preset.bg,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.1) 0%, rgba(15,23,42,0.75) 65%, rgba(15,23,42,0.95) 100%)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 40,
          left: 16,
          right: 16,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: preset.titleFont,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 1.4,
            color: preset.titleColor,
          }}
        >
          {title}
        </Typography>

        {!!date && (
          <Typography
            sx={{
              mt: 0.3,
              fontFamily: preset.dateFont,
              fontSize: 12,
              fontWeight: 500,
              color: preset.dateColor,
            }}
          >
            {date}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 142,
          left: "50%",
          transform: "translateX(-50%)",
          width: "84%",
          aspectRatio: "1 / 1",
          overflow: "hidden",
          backgroundColor: "#020617",
          border: "1px solid rgba(255,255,255,0.28)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.85)",
        }}
      >
        <Box
          component="img"
          src={photoBg}
          alt="Cover"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "50% 20%",
            display: "block",
            filter: isPlaceholder
              ? `brightness(0.7) ${getPhotoBlur(
                  isPlaceholder,
                  6.8
                )}`
              : "none",
            opacity: getPhotoOpacity(isPlaceholder),
          }}
        />
      </Box>

      <PreviewButtons preset={preset} />
    </>
  );
}

function ChampagneLayout({
  preset,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const photoBg = coverPhotoUrl || DEFAULT_COVER_URL;

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: preset.bg,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.05) 0%, rgba(15,23,42,0.65) 70%, rgba(0,0,0,0.9) 100%)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 40,
          left: 16,
          right: 16,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: preset.titleFont,
            fontSize: 19,
            fontWeight: 600,
            letterSpacing: 1.4,
            color: preset.titleColor,
          }}
        >
          {title}
        </Typography>

        {!!date && (
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: preset.dateFont,
              fontSize: 12,
              fontWeight: 500,
              color: preset.dateColor,
            }}
          >
            {date}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 142,
          left: "50%",
          transform: "translateX(-50%)",
          width: "84%",
          aspectRatio: "1 / 1",
          overflow: "hidden",
          boxShadow: "0 16px 40px rgba(0,0,0,0.55)",
        }}
      >
        <Box
          component="img"
          src={photoBg}
          alt="Cover"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "50% 20%",
            display: "block",
            filter: isPlaceholder
              ? `brightness(0.7) ${getPhotoBlur(
                  isPlaceholder,
                  6.8
                )}`
              : "none",
            opacity: getPhotoOpacity(isPlaceholder),
          }}
        />
      </Box>

      <PreviewButtons
        preset={preset}
        primaryBackground="linear-gradient(90deg, #fef3c7, #fed7e2)"
        primaryColor="#111827"
        secondaryBackground="transparent"
        secondaryColor="#fef2f2"
        secondaryBorder="1.5px solid rgba(254,202,202,0.9)"
      />
    </>
  );
}

function ClassicLayout({
  preset,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const photoBg = coverPhotoUrl || DEFAULT_COVER_URL;

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: preset.bg,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 32,
          left: 16,
          right: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "#9ca3af",
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 1.6,
          }}
        >
          Classic
        </Typography>

        <Typography sx={{ fontSize: 11 }}>
          Guest view
        </Typography>
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 52,
          left: 16,
          right: 16,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: preset.titleFont,
            fontSize: 19,
            fontWeight: 600,
            letterSpacing: 0.6,
            color: preset.titleColor,
          }}
        >
          {title}
        </Typography>

        {!!date && (
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: preset.dateFont,
              fontSize: 12,
              fontWeight: 400,
              color: preset.dateColor,
            }}
          >
            {date}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 142,
          left: "50%",
          transform: "translateX(-50%)",
          width: "84%",
          aspectRatio: "1 / 1",
          overflow: "hidden",
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.9), rgba(15,23,42,0.98))",
          boxShadow: "0 18px 46px rgba(0,0,0,0.8)",
        }}
      >
        <Box
          component="img"
          src={photoBg}
          alt="Cover"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "50% 20%",
            display: "block",
            filter: isPlaceholder
              ? `brightness(0.7) ${getPhotoBlur(
                  isPlaceholder,
                  6.8
                )}`
              : "none",
            opacity: getPhotoOpacity(isPlaceholder),
          }}
        />
      </Box>

      <PreviewButtons preset={preset} />
    </>
  );
}

export function CoverPhonePreview({
  presetId,
  coverPhotoUrl,
  title,
  date,
  isPlaceholder,
}) {
  const preset =
    STYLE_PRESETS.find((item) => item.id === presetId) ||
    STYLE_PRESETS[0];

  const layoutProps = {
    preset,
    coverPhotoUrl,
    title,
    date,
    isPlaceholder,
  };

  return (
    <PhoneShell>
      {preset.layout === "western" ? (
        <WesternLayout {...layoutProps} />
      ) : preset.layout === "color" ? (
        <ColorLayout {...layoutProps} />
      ) : preset.layout === "frozen" ? (
        <FrozenLayout {...layoutProps} />
      ) : preset.layout === "polaroid" ? (
        <PolaroidLayout {...layoutProps} />
      ) : preset.layout === "champagne" ? (
        <ChampagneLayout {...layoutProps} />
      ) : preset.layout === "classic" ? (
        <ClassicLayout {...layoutProps} />
      ) : (
        <FullBleedLayout {...layoutProps} />
      )}
    </PhoneShell>
  );
}

function StyleCard({
  preset,
  active,
  coverPhotoUrl,
  title,
  date,
  onClick,
  isPlaceholder,
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        minWidth: 220,
        px: 0.75,
        py: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
        transform: active
          ? "translateY(-4px)"
          : "translateY(0)",
        transition:
          "transform 0.18s ease, box-shadow 0.18s ease",
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: 32,
          background: "transparent",
          border: active
            ? "2px solid rgba(255,255,255,0.65)"
            : "1px solid rgba(148,163,184,0.22)",
          boxShadow: active
            ? "0 22px 70px rgba(0,0,0,0.9)"
            : "0 16px 45px rgba(0,0,0,0.7)",
          transition:
            "border 0.18s ease, box-shadow 0.18s ease",
        }}
      >
        {active && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 30,
              width: 22,
              height: 22,
              borderRadius: 999,
              background: "rgba(15,23,42,0.9)",
              border:
                "1px solid rgba(255,255,255,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 10px 20px rgba(0,0,0,0.55)",
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 16,
                color: "#ffffff",
              }}
            />
          </Box>
        )}

        <CoverPhonePreview
          presetId={preset.id}
          coverPhotoUrl={coverPhotoUrl}
          title={title}
          date={date}
          isPlaceholder={isPlaceholder}
        />
      </Box>

      <Typography
        sx={{
          mt: 0.5,
          fontSize: 14,
          fontWeight: active ? 700 : 600,
color: active ? "#ffffff" : "rgba(255,255,255,0.78)",
          letterSpacing: active ? 0.2 : 0,
        }}
      >
        {preset.name}
      </Typography>
    </Box>
  );
}

const CoverStylePicker = ({
  open,
  onClose,
  eventDetails,
  customizationData,
  onApply,
}) => {
  const theme = useTheme();

  const shortViewport = useMediaQuery(
    "(max-height: 720px)"
  );

  const veryShortViewport = useMediaQuery(
    "(max-height: 640px)"
  );

  const narrowViewport = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const previewScale = useMemo(() => {
    if (!narrowViewport && !shortViewport) return 1;
    if (veryShortViewport) return 0.78;
    if (shortViewport) return 0.86;
    return 0.92;
  }, [
    narrowViewport,
    shortViewport,
    veryShortViewport,
  ]);

  const title =
    eventDetails?.eventName || "Your Event";

  const date = eventDetails?.eventDate || "";

  const coverPhotoUrlRaw =
    (typeof customizationData?.coverPhotoUrl ===
      "string" &&
      customizationData.coverPhotoUrl.trim()) ||
    "";

  const coverPhotoDataUrl =
    typeof customizationData?.coverPhotoDataUrl ===
      "string" &&
    customizationData.coverPhotoDataUrl.startsWith(
      "data:"
    )
      ? customizationData.coverPhotoDataUrl
      : "";

  const coverPhotoPreviewUrl =
    typeof customizationData?.coverPhotoPreviewUrl ===
      "string" &&
    customizationData.coverPhotoPreviewUrl.startsWith(
      "blob:"
    )
      ? customizationData.coverPhotoPreviewUrl
      : "";

  const coverPhotoUrl =
    coverPhotoUrlRaw ||
    coverPhotoDataUrl ||
    coverPhotoPreviewUrl ||
    DEFAULT_COVER_URL;

  const isPlaceholderCover =
    !coverPhotoUrlRaw &&
    !coverPhotoDataUrl &&
    !coverPhotoPreviewUrl;

  const activeId =
    customizationData?.coverStyleId || "scarlet";

  const handleSelect = (preset) => {
    const merged = buildCustomizationFromPreset(
      preset,
      customizationData || {}
    );

    if (onApply) {
      onApply(merged);
    }
  };

  const headerLabel = useMemo(() => {
    const activePreset =
      STYLE_PRESETS.find(
        (preset) => preset.id === activeId
      ) || STYLE_PRESETS[0];

    return activePreset.name;
  }, [activeId]);

  const compactHeader = shortViewport;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: "#020617",
          color: "#e5e7eb",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          px: 2,
          pt: compactHeader ? 1.25 : 2,
          pb: compactHeader ? 1 : 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: "#6b7280",
              letterSpacing: 1,
            }}
          >
            COVER STYLES
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: "#f9fafb",
              fontWeight: 700,
            }}
          >
            Edit your cover screen
          </Typography>

          {!compactHeader && (
            <Typography
              variant="body2"
              sx={{
                color: "#9ca3af",
                mt: 0.5,
              }}
            >
              Tap a style to preview. Your photo stays the
              same — only the vibe changes.
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={onClose}
          sx={{ color: "#e5e7eb" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {!compactHeader && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#e5e7eb",
              fontWeight: 600,
            }}
          >
            {headerLabel}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: 1,
          pt: compactHeader ? 0.5 : 1,
          pb: compactHeader ? 1 : 2,
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            pb: compactHeader ? 1 : 2,
            minWidth: "min-content",
            alignItems: "flex-start",
            transform: `scale(${previewScale})`,
            transformOrigin: "top left",
          }}
        >
          {STYLE_PRESETS.map((preset) => (
            <StyleCard
              key={preset.id}
              preset={preset}
              active={preset.id === activeId}
              coverPhotoUrl={coverPhotoUrl}
              title={title}
              date={date}
              onClick={() => handleSelect(preset)}
              isPlaceholder={isPlaceholderCover}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          px: 2,
          pb: compactHeader ? 1.5 : 2.5,
          pt: compactHeader ? 1 : 1.5,
          borderTop:
            "1px solid rgba(148,163,184,0.25)",
          backdropFilter: "blur(18px)",
          background:
            "linear-gradient(to top, rgba(15,23,42,0.98), rgba(15,23,42,0.85), transparent)",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 16,
            borderRadius: 999,
            py: compactHeader ? 1.05 : 1.2,
            background:
              "linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #8b5cf6 100%)",
            boxShadow:
              "0 14px 40px rgba(0,0,0,0.8)",
            "&:hover": {
              background:
                "linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #8b5cf6 100%)",
              opacity: 0.99,
            },
          }}
        >
          Done
        </Button>
      </Box>
    </Dialog>
  );
};

export default CoverStylePicker;